import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useKeycloak } from '@react-keycloak/web';
import { authenticatedFetch } from '../../../api/authenticatedFetch';

// Check if we're in print mode (for PDF generation)
const urlParams = new URLSearchParams(window.location.search);
const isPrintMode = urlParams.get('print') === 'true';
const hasInjectedToken = urlParams.get('token') !== null;

// Default staleTime for widget queries, matching the global QueryClient
// default (see src/index.js) so figure/dashboard widgets — the slow fetches
// we most want to keep fresh longer within a session — actually benefit from
// the longer window instead of being overridden back down to 5 minutes.
const DEFAULT_WIDGET_STALE_TIME = 15 * 60 * 1000; // 15 minutes

/**
 * Custom hook for widget data fetching using React Query
 * Supports both static data (via props) and dynamic data (via API)
 * Token refresh is handled centrally by authenticatedFetch
 *
 * @param {object} config - widget config (dataSource, id, title, ...)
 * @param {Array} filters - runtime filters merged into the request body
 * @param {object} [options]
 * @param {number} [options.staleTime] - overrides the default staleTime (15 min)
 */
const useWidgetData = (config, filters = [], options = {}) => {
  const { keycloak } = useKeycloak();
  const dataSource = config?.dataSource;

  // Build query function
  const queryFn = useCallback(async () => {
    if (!dataSource) {
      return null;
    }

    // Handle static data
    if (dataSource.type === 'static') {
      return dataSource.data || null;
    }

    // Handle API data
    if (dataSource.type === 'api') {
      // Build request body for POST requests
      let requestBody = null;
      if (dataSource.method === 'POST' && dataSource.body) {
        const existingFilters = (dataSource.body && Array.isArray(dataSource.body.filters)) ? dataSource.body.filters : [];
        const runtimeFilters = Array.isArray(filters) ? filters : [];
        const allFilters = [...existingFilters, ...runtimeFilters];
        
        requestBody = {
          ...dataSource.body,
          // Merge filters from config body and runtime filters instead of overwriting
          filters: allFilters
        };

        // Remove the useDynamicDates flag from the request body (it's just for internal use)
        delete requestBody.useDynamicDates;
        
        // Helper function to format date as YYYY-MM-DD in local timezone
        const formatDateLocal = (date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        // Calculate dynamic dates for forecast endpoints or when useDynamicDates is set
        const needsDynamicDates = (dataSource.endpoint && dataSource.endpoint.includes('forecast')) || 
                                   dataSource.body?.useDynamicDates;
        
        if (needsDynamicDates) {
          const now = new Date();
          
          // End of next month (to include next month in forecast)
          const endOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 2, 0);
          const endDate = formatDateLocal(endOfNextMonth);
          
          // Start of previous month (gives ~3 months of data: previous, current, and next month)
          const startOfPreviousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          const startDate = formatDateLocal(startOfPreviousMonth);
          
          requestBody.start_date = startDate;
          requestBody.end_date = endDate;
          
          if (isPrintMode) {
            console.log('useWidgetData: Added dynamic dates for ' + dataSource.endpoint + ': ' + JSON.stringify({ startDate, endDate }));
          }
        }
      }

      // Build URL with query parameters
      let endpoint = dataSource.endpoint;
      if (dataSource.params) {
        const searchParams = new URLSearchParams(dataSource.params);
        endpoint += `?${searchParams.toString()}`;
      }

      // Use authenticated fetch (handles token refresh automatically)
      if (isPrintMode) {
        console.log('useWidgetData: Making API request: ' + JSON.stringify({
          configId: config?.id,
          configTitle: config?.title,
          endpoint,
          method: dataSource.method || 'GET',
          bodyKeys: requestBody ? Object.keys(requestBody) : null
        }));
      }
      
      // Use authenticated fetch (has its own 15s timeout in print mode)
      const response = await authenticatedFetch(endpoint, {
        method: dataSource.method || 'GET',
        body: requestBody,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('useWidgetData: API error: ' + JSON.stringify({
          status: response.status,
          endpoint,
          error: errorText.substring(0, 200)
        }));
        throw new Error(`API error: ${response.status}`);
      }

      if (isPrintMode) {
        console.log('useWidgetData: Reading response body for ' + endpoint);
      }
      
      const responseData = await response.json();
      
      if (isPrintMode) {
        console.log('useWidgetData: API response received: ' + JSON.stringify({
          endpoint,
          hasData: !!responseData,
          dataType: typeof responseData,
          dataKeys: responseData ? Object.keys(responseData).slice(0, 5) : []
        }));
      }

      // Apply data transformation if provided AND if it's actually a function
      // (transform functions from sidebar config might not survive database serialization)
      if (dataSource.transform && typeof dataSource.transform === 'function') {
        try {
          return dataSource.transform(responseData);
        } catch (transformError) {
          console.error('Transform function error:', transformError);
          // Fall back to raw data if transform fails
          return responseData;
        }
      }
      return responseData;
    }

    return null;
  }, [dataSource, filters, config?.id, config?.title]);

  // Determine if the query should be enabled
  // In print mode with injected token, always enable (token is already set on keycloak object)
  // Otherwise, require keycloak token to be present
  const hasValidToken = isPrintMode && hasInjectedToken 
    ? true 
    : (keycloak?.token !== '' && keycloak?.token !== undefined);

  // Determine if query should be enabled
  const isEnabled = !!dataSource && (dataSource.type === 'static' || hasValidToken);

  // Debug logging for print mode
  if (isPrintMode) {
    // Create a stable query key string for debugging
    const queryKeyDebug = JSON.stringify(['widgetData', config?.id, filters?.length || 0, dataSource?.endpoint]);
    console.log('useWidgetData (print mode): ' + JSON.stringify({
      configId: config?.id,
      configTitle: config?.title,
      hasDataSource: !!dataSource,
      dataSourceType: dataSource?.type,
      dataSourceEndpoint: dataSource?.endpoint,
      hasValidToken,
      isEnabled,
      queryKey: queryKeyDebug
    }));
  }

  // Use React Query for data fetching
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['widgetData', config?.id, filters, dataSource],
    queryFn,
    enabled: isEnabled,
    staleTime: options?.staleTime ?? DEFAULT_WIDGET_STALE_TIME,
    refetchOnWindowFocus: false,
  });

  // Debug logging for print mode - query state
  if (isPrintMode) {
    console.log('useWidgetData query state (print mode): ' + JSON.stringify({
      configId: config?.id,
      isLoading,
      hasData: !!data,
      hasError: !!error,
      errorMessage: error?.message
    }));
  }

  return {
    data,
    isLoading,
    error: error ? (error.message || 'Failed to fetch data') : null,
    refetch
  };
};

export default useWidgetData;
